import { Component, EventEmitter, OnChanges } from '@angular/core';
import { filter } from 'rxjs/operators';
import { BaseComponent } from '../../shared/base-components/base.component';
import { OmegaDetailData, OmegaDetailModalComponent, UpdateType } from '../../shared/components/omega/omega-details-update-modal.component';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerIndividualNameSuffixType } from '../models/enums/customer-individual-name-suffix-type.enum';
import { CustomerNameType } from '../models/enums/customer-name-type.enum';
import { CustomerType } from '../models/enums/customer-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerNameContext, CustomerNameModalComponent } from './customer-name-modal.component';

@Component({
    selector: 'customer-names',
    templateUrl: './customer-names.html',
    inputs: ['customerNumber', 'names', 'customerTypeId', 'canEdit', 'fullNameList'],
    outputs: ['addedName', 'editedName']
})

export class CustomerNamesComponent extends BaseComponent implements OnChanges {
    public names: any[];
    public customerNumber: number;
    public customerTypeId: number;
    public viewNamesTitle: string;
    public showViewNames = false;
    public filterNames: any[] = [];
    public canEdit: boolean;
    public isRestricted = false;
    public viewAdditionalNames = false;

    private fullNameList: any[];
    private suffixTypes = new CustomerIndividualNameSuffixType();
    private nameTypes = new CustomerNameType();
    private customerTypes: CustomerType = new CustomerType();

    public addedName: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
    public editedName: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
    public businessTransactionNumberId: number;

    constructor(
        public modalService: MvdModalService,
        private customerService: CustomerManageService) {
        super();
    }

    async ngOnInit() {
        await this.loadInitData();
        this.viewNamesTitle = (this.customerTypeId === this.customerTypes.Organization.id) ? 'View DBA Names' : 'View AKA Names';
    }

    async ngOnChanges() {
        await this.loadInitData();
    }

    private async loadInitData() {
        this.getRestricted();

        if (this.fullNameList !== undefined) {
            this.names = this.filterPrimary();
            this.filterAkaDbaNames();
            this.denormalizeData();
        } else if (this.customerNumber != null) {
            this.populateNames(this.customerNumber);
        }

        if (this.customerTypeId === undefined && this.customerNumber !== undefined) {
            this.getCustomerType();
        }
    }

    private filterAkaDbaNames(){
        this.filterNames = this.filterViewNames();
        this.viewAdditionalNames = (this.filterNames && this.filterNames.length > 0) ? true : false;
    }

    populateNames(customerNumberId: number) {
        this.customerService.getNames(customerNumberId)
            .subscribe(result => {
                this.fullNameList = result;
            },
            e => console.log('error', e),
            () => {
                this.names = this.filterPrimary();
                this.filterAkaDbaNames();
                if (this.showViewNames) {
                    this.names = this.names.concat(this.filterNames);
                }
                this.denormalizeData();
            });
    }

    toggleViewNames() {
        if (this.showViewNames) {
            this.showViewNames = false;
            this.viewNamesTitle = (this.customerTypeId === this.customerTypes.Individual.id) ? 'View AKA Names' : 'View DBA Names';

            this.populateNames(this.customerNumber);
        } else {
            this.showViewNames = true;
            this.viewNamesTitle = (this.customerTypeId === this.customerTypes.Individual.id) ? 'Hide AKA Names' : 'Hide DBA Names';

            this.populateNames(this.customerNumber);
        }
    }

    filterDbas(): any[] {
        const self = this;
        return this.fullNameList.filter(function(dba) {
            if (null == dba) {
                return false;
            }

            return dba.customerNameTypeId == self.nameTypes.DBA.id;
        });
    }

    filterViewNames(): any[] {
        const self = this;
        return this.fullNameList.filter(function(aka) {
            if (null == aka) {
                return false;
            }

            return aka.customerNameTypeId == ((self.customerTypeId === self.customerTypes.Individual.id) ? self.nameTypes.AKA.id : self.nameTypes.DBA.id);
        });
    }

    filterPrimary(): any[] {
        const self = this;
        return this.fullNameList.filter(function(primary) {
            if (null == primary) {
                return false;
            }
            return primary.customerNameTypeId == self.nameTypes.LegalName.id || primary.customerNameTypeId == self.nameTypes.OrganizationName.id;
        });
    }

    private getCustomerType() {
        this.customerService.getCustomerType(this.customerNumber)
            .subscribe(result => {
                this.customerTypeId = result;
                this.viewNamesTitle = (this.customerTypeId === this.customerTypes.Individual.id) ? 'View AKA Names' : 'View DBA Names';
            },
            e => this.handleValidationResult(e),
            () => {
            });
    }

    private denormalizeData() {
        for (const name of this.names) {
            const suffix = this.getValues(name.customerIndividualNameSuffixTypeId, this.suffixTypes);
            name.customerNameType = this.getValues(name.customerNameTypeId, this.nameTypes);

            if (name.customerNameTypeId == this.nameTypes.OrganizationName.id || name.customerNameTypeId == this.nameTypes.DBA.id) {
                name.customerName = name.organizationName;
            } else {
                name.customerName = (name.firstName != null ? name.firstName : '') + (name.middleName != null ? ' ' + name.middleName : '') + ' ' + name.lastName + ' ' + suffix;
            }
        }
    }

    getValues(id: number, types: any): string {
        if (id !== undefined && id !== null) {
            return types.FromId(id).displayValue;
        } else {
            return '';
        }
    }

    addName() {
        const data = new CustomerNameContext(0, this.customerNumber, this.customerTypeId, false);
        this.modalService.open(CustomerNameModalComponent, data, ModalSize.Large).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(
                () => {
                    this.populateNames(this.customerNumber);
                    this.editedName.emit();
                }
            );
    }

    editName(id) {
        const data = new CustomerNameContext(id, this.customerNumber, this.customerTypeId, false);
        this.modalService.open(CustomerNameModalComponent, data, ModalSize.Large).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(
                () => {
                    this.populateNames(this.customerNumber);
                    this.editedName.emit();
                }
            );
    }

    private getRestricted() {
        if (this.customerNumber != undefined && this.customerNumber != null) {
            this.customerService.isCustomerRestricted(this.customerNumber).subscribe(
                r => this.isRestricted = r.result,
                e => this.handleValidationResult(e)
            );
        }
    }

    public refreshData() {
        this.customerService.bustCache(this.customerNumber)
            .subscribe(
                r => window.location.reload(),
                e => this.handleValidationResult(e)
            );
    }

    public launchUpdateDetailsModal() {
        const data = new OmegaDetailData(this.customerNumber, UpdateType.Customer);
        this.modalService.open(OmegaDetailModalComponent, data);
    }
}
